import type { InvariantSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync } from '../util/safe.js';

/**
 * Проверяемые тождества.
 *
 * Обычные отпечатки — canvas, WebGL, звук — различаются от устройства к
 * устройству, и потому подделываются безнаказанно: подмешанный шум просто
 * выглядит как ещё одна видеокарта. Отличить его от настоящего разнообразия
 * можно только накопленной статистикой, а она набирается месяцами.
 *
 * Но есть величины, чьё правильное значение известно не из наблюдений, а по
 * построению. Холст, залитый сплошным цветом, обязан вернуть ровно этот цвет —
 * на любой видеокарте, в любом браузере, при любых настройках. Отрисованная
 * тишина обязана состоять ровно из нулей. Прямоугольник шириной в сто пикселей
 * обязан иметь ширину сто.
 *
 * Это не статистика и не догадка: разнообразию здесь просто неоткуда взяться.
 * Любое отклонение означает, что между вычислением и ответом кто-то вмешался.
 *
 * Замеры намеренно устроены так, чтобы не зависеть ни от видеокарты, ни от
 * сглаживания, ни от цветового профиля. Никаких градиентов, полупрозрачности,
 * поворотов и дробных координат — только то, что обязано совпасть в точности.
 *
 * Проверено на живом движке до выкладки: Chromium на macOS дал ровно нули по
 * всем трём замерам. Утверждать такое по памяти было нельзя — именно на
 * подобных утверждениях мы трижды за сутки обвинили настоящие браузеры.
 */

/** Цвет для заливки: непрозрачный, без полупрозрачности и смешивания. */
const FILL = { r: 17, g: 187, b: 68, a: 255 };

/**
 * Читает пиксель из холста, залитого сплошным цветом.
 *
 * Возвращает разницу с ожидаемым по каждому каналу. У настоящего браузера
 * все четыре нуля, и других вариантов не бывает: заливка не проходит ни
 * через сглаживание, ни через цветовое преобразование.
 */
function canvasDeviation(): { channels: number; worst: number; pixels: number } | null {
  return attempt(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    // willReadFrequently убирает путь через видеопамять: чтение идёт из того
    // же буфера, куда шла запись, и лишних преобразований на пути нет.
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.fillStyle = `rgb(${FILL.r} ${FILL.g} ${FILL.b})`;
    ctx.fillRect(0, 0, 32, 32);

    // Читается весь холст, а не один пиксель.
    //
    // Первая версия брала единственный пиксель из середины и на живой
    // антидетект-сборке ничего не показала: шум подмешивается выборочно, и
    // один отсчёт легко его не застаёт. Тысяча отсчётов вместо одного —
    // та же проверка, но с несравнимо большей вероятностью заметить.
    const data = ctx.getImageData(0, 0, 32, 32).data;
    const want = [FILL.r, FILL.g, FILL.b, FILL.a];

    let channels = 0;
    let worst = 0;
    for (let i = 0; i < data.length; i += 4) {
      for (let k = 0; k < 4; k++) {
        const deviation = Math.abs(data[i + k] - want[k]);
        if (deviation > 0) {
          channels++;
          if (deviation > worst) worst = deviation;
        }
      }
    }
    return { channels, worst, pixels: data.length / 4 };
  });
}

/**
 * Проверяет геометрию простого прямоугольника.
 *
 * Элемент с целыми размерами в пикселях обязан вернуть ровно эти числа:
 * раскладка считается в тех же единицах, округлений на пути нет.
 */
function rectDeviation(): number[] | null {
  return attempt(() => {
    const el = document.createElement('div');
    el.style.cssText =
      'position:absolute;left:0;top:0;width:100px;height:50px;' +
      'padding:0;border:0;margin:0;transform:none;';
    document.body.appendChild(el);
    try {
      const rect = el.getBoundingClientRect();
      return [rect.width - 100, rect.height - 50];
    } finally {
      el.remove();
    }
  });
}

/**
 * Пропускает известный сигнал через звуковой движок и сверяет выход со входом.
 *
 * Буфер соединён с выходом напрямую: ни усиления, ни сжатия, ни изменения
 * частоты — преобразовывать нечего, и выход обязан совпасть со входом до
 * последнего бита.
 *
 * Первая версия отрисовывала тишину, и живая антидетект-сборка её прошла:
 * шум подмешивается там, где считается отпечаток, а пустая запись такой не
 * выглядит. Настоящий сигнал выглядит именно так, оставаясь при этом
 * проверяемым в точности.
 */
async function audioDeviation(): Promise<{ samples: number; worst: number } | null> {
  return attemptAsync(async () => {
    const Ctx =
      (window as unknown as Record<string, unknown>).OfflineAudioContext ??
      (window as unknown as Record<string, unknown>).webkitOfflineAudioContext;
    if (typeof Ctx !== 'function') return null;

    const length = 4096;
    const rate = 44100;
    const ctx = new (Ctx as new (c: number, l: number, r: number) => OfflineAudioContext)(
      1,
      length,
      rate,
    );

    const buffer = ctx.createBuffer(1, length, rate);
    const input = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) input[i] = Math.sin(i / 40) * 0.5;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start();

    const output = (await ctx.startRendering()).getChannelData(0);

    let samples = 0;
    let worst = 0;
    for (let i = 0; i < length; i++) {
      const deviation = Math.abs(output[i] - input[i]);
      if (deviation > 0) {
        samples++;
        if (deviation > worst) worst = deviation;
      }
    }
    return { samples, worst };
  });
}

/**
 * Круговой путь холста: отрисовка — выгрузка в PNG — обратная загрузка.
 *
 * PNG сжимает без потерь, поэтому вернувшееся изображение обязано совпасть с
 * исходным до последнего значения. Проверка добавлена по итогам живого
 * замера: прямое чтение залитого холста антидетект-сборка прошла, потому что
 * шум подмешивается не ко всякой отрисовке. Здесь путь идёт через toDataURL —
 * метод, который такие сборки перехватывают чаще всего, — а на холсте есть
 * содержание, и для них он выглядит настоящим отпечатком.
 */
async function canvasRoundTrip(): Promise<{ values: number; worst: number } | null> {
  return attemptAsync(async () => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.fillStyle = `rgb(${FILL.r} ${FILL.g} ${FILL.b})`;
    ctx.fillRect(0, 0, 32, 32);
    // Второй прямоугольник — чтобы холст не выглядел пустой заливкой.
    // Координаты и размеры целые: сглаживанию взяться неоткуда.
    ctx.fillStyle = 'rgb(200 30 90)';
    ctx.fillRect(4, 4, 8, 8);

    const url = canvas.toDataURL('image/png');
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('изображение не загрузилось'));
      image.src = url;
    });

    const back = document.createElement('canvas');
    back.width = 32;
    back.height = 32;
    const backCtx = back.getContext('2d', { willReadFrequently: true });
    if (!backCtx) return null;
    backCtx.drawImage(image, 0, 0);

    const before = ctx.getImageData(0, 0, 32, 32).data;
    const after = backCtx.getImageData(0, 0, 32, 32).data;

    let values = 0;
    let worst = 0;
    for (let i = 0; i < before.length; i++) {
      const deviation = Math.abs(before[i] - after[i]);
      if (deviation > 0) {
        values++;
        if (deviation > worst) worst = deviation;
      }
    }
    return { values, worst };
  });
}

export const invariantsModule = defineModule({
  id: 'invariants',
  phase: 2,
  timeoutMs: 3000,
  async run(): Promise<InvariantSignals> {
    const canvas = canvasDeviation();
    const rect = rectDeviation();
    const audio = await audioDeviation();
    const roundTrip = await canvasRoundTrip();

    return {
      canvasRoundTripValues: roundTrip?.values ?? null,
      canvasRoundTripWorst: roundTrip?.worst ?? null,
      canvasBadChannels: canvas?.channels ?? null,
      canvasWorstDeviation: canvas?.worst ?? null,
      canvasPixels: canvas?.pixels ?? null,
      rectDeviation: rect,
      audioBadSamples: audio?.samples ?? null,
      audioWorstDeviation: audio?.worst ?? null,
    };
  },
});
